import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, CloseIcon, MutedSpeakerIcon, StreamrNewLogoSolo, UnmutedSpeakerIcon, } from '@streamr/tv-ui';
import styled from 'styled-components';
import { MediaService } from '../../services/media/MediaService';
import { StreamrAppService } from '../../services/streamrapp/StreamrAppService';
import { UserService } from '../../services/user/UserService';
import { AudioDeviceButton, VideoDeviceButton } from '../DeviceButton';
import { LiveIndicator } from '../LiveIndicator';
import { confirm } from '../popups/ConfirmationPopup';
import { selectCamera } from '../popups/devices/CameraSelectorPopup';
import { selectMicrophone } from '../popups/devices/MicrophoneSelectorPopup';
import { ShareButtonRound } from '../ShareButtons';
import { BottomLeftSlot, BottomRightSlot, HudSlots, TopLeftSlot, TopRightSlot, } from '../shared/HudSlots';
import { LogoLink } from '../shared/LogoLink';
import { MobilePluginButtonTray } from '../shared/PluginButtonTray';
export function StreamrAppGuestViewMobileHud({ broadcastUrl, fullscreenButton, visible, }) {
    const isVideoStreaming = StreamrAppService.getOutput().useIsVideoStreaming();
    const mediaService = MediaService.getNotificationInterface();
    const mediaOutputAudioMuted = MediaService.getOutput().useAudioMuted();
    const mediaOutputVideoMuted = MediaService.getOutput().useVideoMuted();
    const connectedVideoDevices = MediaService.getOutput().useConnectedVideoDevices();
    const connectedAudioDevices = MediaService.getOutput().useConnectedAudioDevices();
    const playbackAudioMuted = !!StreamrAppService.getOutput().useAudioMuted();
    const onMuteAudioButtonClicked = () => {
        mediaService.notifyAudioMutingChangeRequested(!mediaOutputAudioMuted);
    };
    const onMuteVideoButtonClicked = () => {
        mediaService.notifyVideoMutingChangeRequested(!mediaOutputVideoMuted);
    };
    return (_jsxs(Hud, { "$visible": visible, children: [_jsx(TopLeftSlot, { children: _jsx(LogoLink, { logoImage: _jsx(StreamrNewLogoSolo, {}) }) }), _jsxs(TopRightSlot, { children: [_jsx(GuestBadge, { children: "Guest" }), isVideoStreaming && _jsx(LiveIndicator, {})] }), _jsxs(StyledBottomLeftSlot, { children: [_jsx(PlaybackMuteButton, { type: "button", onClick: () => {
                            StreamrAppService.getNotificationInterface().notifyToggleAudio();
                        }, "$isMuted": playbackAudioMuted, children: playbackAudioMuted ? (_jsx(MutedSpeakerIcon, {})) : (_jsx(UnmutedSpeakerIcon, {})) }), _jsx(AudioDeviceButton, { devicePresent: connectedAudioDevices.length > 0, muted: mediaOutputAudioMuted, onMuteClick: onMuteAudioButtonClicked, onSelectClick: (anchor) => {
                            selectMicrophone({
                                anchorRef: anchor,
                                variant: 'top-center',
                            });
                        } }), _jsx(VideoDeviceButton, { devicePresent: connectedVideoDevices.length > 0, muted: mediaOutputVideoMuted, onMuteClick: onMuteVideoButtonClicked, onSelectClick: (anchor) => {
                            selectCamera({
                                anchorRef: anchor,
                                variant: 'top-center',
                            });
                        } }), _jsx(LeaveStageButton, { type: "button", onClick: async () => {
                            if (await confirm({
                                title: 'Leave stage',
                                message: 'Your camera and microphone will be turned off.',
                                okLabel: 'Leave',
                                cancelLabel: 'Cancel',
                            })) {
                                UserService.getNotificationInterface().notifyLeaveStage();
                            }
                        }, children: _jsx(CloseIcon, {}) })] }), _jsxs(StyledBottomRightSlot, { children: [fullscreenButton, _jsx(ShareButtonRound, { url: broadcastUrl }), _jsx(MobilePluginButtonTray, { slotId: "buttonstray" })] })] }));
}
const Hud = styled(HudSlots) `
    --tv-HudSlots-offset: 1rem;
    z-index: 10;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.3s ease-in-out;
    pointer-events: none;
`;
const GuestBadge = styled.span `
    background-color: var(--tv-ui-color-12);
    color: var(--tv-ui-color-3);
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0.625rem;
    border-radius: 1rem;
    backdrop-filter: blur(10px);
    pointer-events: auto;
`;
const LeaveStageButton = styled(Button) `
    --Button_Background: var(--tv-ui-color-6);
    --Button_HoverBackground: var(--tv-ui-color-6);
    --Button_TextColor: var(--tv-ui-color-3);
    --Button_BorderRadius: 1.5rem;
    --Button_Height: 3rem;
    --Button_Width: 3rem;
    backdrop-filter: blur(10px);
`;
const PlaybackMuteButton = styled(Button) `
    --Button_Background: ${({ $isMuted }) => $isMuted ? 'var(--tv-ui-color-3)' : 'var(--tv-ui-color-12)'};
    --Button_HoverBackground: ${({ $isMuted }) => $isMuted ? 'var(--tv-ui-color-3)' : 'var(--tv-ui-color-12)'};
    --Button_TextColor: ${({ $isMuted }) => $isMuted ? 'var(--tv-ui-color-1)' : 'var(--tv-ui-color-3)'};
    --Button_BorderRadius: 1.5rem;
    --Button_Height: 3rem;
    --Button_Width: 3rem;
    backdrop-filter: blur(10px);
`;
const StyledBottomLeftSlot = styled(BottomLeftSlot) `
    box-sizing: border-box;
    --tv-HudSlots-offset: 0;
    --tv-HudBottomSlot-offset: 1rem;
    width: 100%;
    display: flex;
    gap: 0.5rem;
    padding: 0 1rem;
    align-items: center;
    justify-content: center;

    @media (min-width: 25rem) {
        gap: 0.75rem;
    }

    @media (orientation: landscape) {
        width: auto;
        left: 1rem;
        padding: 0;
    }
`;
const StyledBottomRightSlot = styled(BottomRightSlot) `
    --tv-HudSlots-offset: 0;
    bottom: 50%;
    right: 1rem;
    transform: translateY(50%);
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-end;
    justify-content: center;

    @media (orientation: landscape) {
        bottom: 1rem;
        right: 1rem;
        transform: none;
    }
`;
